import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FaPhone } from "react-icons/fa";
import Logo from "../../assets/img/logo.svg";
import "./Navbar.css";

const LUXURY = [0.16, 1, 0.3, 1];

const navItems = [
  { id: "home",         label: "Home",         href: "/",              serial: "01" },
  { id: "erkenning",    label: "Erkenningen",  href: "#erkenning",     serial: "02" },
  { id: "about",        label: "Over ons",     href: "#about",         serial: "03" },
  { id: "services",     label: "Diensten",     href: "#services",      serial: "04" },
  { id: "vervangwagen", label: "Vervangwagen", href: "#vervangwagen",  serial: "05" },
  { id: "eurogarant",   label: "Eurogarant",   href: "#eurogarant",    serial: "06" },
  { id: "contact",      label: "Contact",      href: "#contact",       serial: "07" },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen]     = useState(false);
  const [isScrolled, setIsScrolled]     = useState(false);
  const [activeSection, setActiveSection] = useState("home");

  /* ── scroll spy + body lock ── */
  useEffect(() => {
    const onScroll = () => {
      setIsScrolled(window.scrollY > 50);
      for (const { id } of navItems) {
        const el = document.getElementById(id);
        if (el) {
          const r = el.getBoundingClientRect();
          if (r.top <= 100 && r.bottom >= 100) { setActiveSection(id); break; }
        }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const handleNavClick = (e, sectionId) => {
    e.preventDefault();
    const el = document.getElementById(sectionId);
    if (el) window.scrollTo({ top: el.offsetTop - 80, behavior: "smooth" });
    setMobileOpen(false);
  };

  /* ── Mobile overlay rendered via portal ── */
  const mobileOverlay = (
    <AnimatePresence>
      {mobileOpen && (
        <motion.div
          className="mobile-overlay"
          key="mobile-overlay"
          initial={{ y: "-100%" }}
          animate={{ y: 0 }}
          exit={{ y: "-100%" }}
          transition={{ duration: 0.55, ease: LUXURY }}
          aria-label="Mobiel menu"
          role="dialog"
          aria-modal="true"
        >
          {/* grain */}
          <div className="mobile-overlay-grain" aria-hidden="true" />

          {/* close button top-right */}
          <button
            className="mobile-overlay-close"
            onClick={() => setMobileOpen(false)}
            aria-label="Menu sluiten"
          >
            <motion.span className="mob-line"
              animate={{ rotate: 45, y: 1 }}
              transition={{ duration: 0.3, ease: LUXURY }}
            />
            <motion.span className="mob-line mob-line--short"
              animate={{ rotate: -45, y: -1 }}
              transition={{ duration: 0.3, ease: LUXURY }}
            />
          </button>

          {/* links */}
          <nav className="mobile-overlay-nav">
            <ul className="mobile-overlay-list">
              {navItems.map(({ id, label, href, serial }, i) => (
                <motion.li
                  key={id}
                  className="mobile-overlay-item"
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, ease: LUXURY, delay: 0.15 + i * 0.08 }}
                >
                  <span className="mob-serial" aria-hidden="true">{serial}</span>
                  <a
                    href={href}
                    className={`mob-link${activeSection === id ? " mob-link--active" : ""}`}
                    onClick={(e) => handleNavClick(e, id)}
                  >
                    {label}
                  </a>
                </motion.li>
              ))}
            </ul>
          </nav>

          {/* contact footer */}
          <div className="mobile-overlay-footer">
            <a href="tel:+32497284662" className="mob-contact-link">
              <FaPhone aria-hidden="true" />
              <span>+32 497 28 46 62</span>
            </a>
            <a href="mailto:kris@carrosseriekris.be" className="mob-contact-link">
              kris@carrosseriekris.be
            </a>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <>
      <motion.header
        className={`modern-navbar${isScrolled ? " scrolled" : ""}`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.7, ease: LUXURY }}
      >
        <div className="navbar-container">

          {/* Logo */}
          <motion.div
            className="navbar-logo"
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.0, ease: LUXURY, delay: 0.1 }}
          >
            <a href="/" onClick={(e) => handleNavClick(e, "home")}>
              <img src={Logo} alt="Carrosserie Kris Logo" />
            </a>
          </motion.div>

          {/* Desktop nav */}
          <nav className="desktop-nav" aria-label="Hoofdnavigatie">
            <ul className="nav-list">
              {navItems.map(({ id, label, href }, i) => (
                <motion.li
                  key={id}
                  className={`nav-item${activeSection === id ? " active" : ""}`}
                  initial={{ opacity: 0, y: -16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.55, delay: i * 0.05, ease: LUXURY }}
                >
                  <a
                    href={href}
                    className={`nav-link${activeSection === id ? " active" : ""}`}
                    onClick={(e) => handleNavClick(e, id)}
                  >
                    {label}
                    <span className="nav-link-underline" aria-hidden="true" />
                  </a>
                </motion.li>
              ))}
            </ul>
          </nav>

          {/* Ghost CTA */}
          <motion.div
            className="navbar-contact"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.55, delay: 0.45, ease: LUXURY }}
          >
            <a href="tel:+32497284662" className="contact-button">
              <FaPhone className="contact-icon" aria-hidden="true" />
              <span className="contact-text">Bel nu</span>
            </a>
          </motion.div>

          {/* Hamburger — two lines of unequal length */}
          <motion.button
            className="mobile-menu-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? "Menu sluiten" : "Menu openen"}
            aria-expanded={mobileOpen}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.3 }}
            whileTap={{ scale: 0.9 }}
          >
            <motion.span
              className="mob-line"
              animate={{ rotate: mobileOpen ? 45 : 0, y: mobileOpen ? 5 : 0 }}
              transition={{ duration: 0.4, ease: LUXURY }}
            />
            <motion.span
              className="mob-line mob-line--short"
              animate={{
                rotate: mobileOpen ? -45 : 0,
                y: mobileOpen ? -5 : 0,
                width: mobileOpen ? "24px" : "16px",
              }}
              transition={{ duration: 0.4, ease: LUXURY }}
            />
          </motion.button>

        </div>
      </motion.header>

      {/* Portal: renders outside header so it covers full viewport */}
      {ReactDOM.createPortal(mobileOverlay, document.body)}
    </>
  );
}
